<script setup lang="ts">
import FooterBar from '@/components/footer-bar/FooterBar.vue'
import CommonBtn from '@/components/common-btn/CommonBtn.vue'
import { useUserStore } from '@/stores/modules/user'
import { formatNumberWithCommas } from '@/utils'
import { socketEmitAsync } from '@/utils/web-socket'
import { ElMessage, dayjs } from 'element-plus'
import type { UpdateNicknameRes } from '@/types/user'
import { useCommonStore } from '@/stores/modules/common'
import { ref } from 'vue'
import NicknameColor from './components/NicknameColor.vue'
import BindEamil from './components/BindEamil.vue'
import ChangePassword from './components/ChangePassword.vue'
import SetPrivacy from './components/SetPrivacy.vue'

const envMode = import.meta.env.VITE_ENV_MODE

const commonStore = useCommonStore()
const userStore = useUserStore()

function handleChangePassword() {
  if (!userStore.email) return ElMessage.warning('修改密码需先绑定邮箱')
  changePasswordShow.value = true
}
function handleLogout() {
  ElMessageBox.confirm('确认退出登录？【请务必确认您知晓该账号的用户名及密码】', '提示', {
    type: 'warning',
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      localStorage.removeItem(import.meta.env.VITE_TOKEN_NAME)
      location.reload()
    })
    .catch(() => {})
}

function handleUseCdkey() {
  if (dayjs().diff(userStore.created, 'hour') < 1) return ElMessage.warning('注册满一小时才可以使用兑换码')

  ElMessageBox.prompt('请输入兑换码', '使用兑换码', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    inputPattern: /^.{2,20}$/,
    inputErrorMessage: '兑换码在2~20字',
    confirmButtonClass: 'is-plain',
    async beforeClose(action, instance, done) {
      if (action !== 'confirm') return done()
      try {
        instance.confirmButtonLoading = true
        instance.confirmButtonText = '加载中...'
        const value = instance.inputValue
        await socketEmitAsync({ type: 'useCdkey', data: { key: value } })
        commonStore.getMailList()
        done()
      } finally {
        instance.confirmButtonLoading = false
        instance.confirmButtonText = '确定'
      }
    },
  })
}

function handleUpdateNickname() {
  if (userStore.gold <= 50000) return ElMessage.warning('修改昵称需要 50,000 金币')
  ElMessageBox.prompt('请输入您的新昵称，本次修改花费 50,000 金币', '修改昵称', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    inputPattern: /^[\u4e00-\u9fa5a-zA-Z0-9]{2,8}$/,
    inputValue: userStore.nickname,
    inputErrorMessage: '昵称在2~8字，且只允许中英文及数字',
    confirmButtonClass: 'is-plain',
    async beforeClose(action, instance, done) {
      if (action !== 'confirm') return done()
      try {
        instance.confirmButtonLoading = true
        instance.confirmButtonText = '加载中...'
        const value = instance.inputValue
        const { nickname, gold } = await socketEmitAsync<UpdateNicknameRes>({ type: 'updateNickname', data: { nickname: value } })
        userStore.nickname = nickname
        userStore.gold = gold
        done()
      } finally {
        instance.confirmButtonLoading = false
        instance.confirmButtonText = '确定'
      }
    },
  })
}

function getToken() {
  ElMessageBox.alert(`您的唯一标识：<br /><span class="can-select">${userStore.token}</span><br />请妥善保存，切勿泄露！`, '温馨提示', {
    icon: 'warning',
    confirmButtonText: '复制',
    confirmButtonClass: 'is-plain',
    dangerouslyUseHTMLString: true,
    beforeClose: async (_action, _instance, done) => {
      try {
        await navigator.clipboard.writeText(userStore.token)
        ElMessage.success('复制成功')
        done()
      } catch {
        ElMessage.error('复制失败，请手动复制文本')
        done()
      }
    },
  })
}

const uiZoomLocalKey = `war-soul-${envMode === 'test' ? 'test-' : ''}ui-zoom`
const uiZoom = ref(localStorage.getItem(uiZoomLocalKey) || '1')
function handleChangeUiZoom(val: string) {
  document.documentElement.style.setProperty('zoom', val)
  localStorage.setItem(uiZoomLocalKey, uiZoom.value)
  ElMessage.success('设置成功')
}

const uiModeLocalKey = `war-soul-${envMode === 'test' ? 'test-' : ''}ui-mode`
const uiMode = ref(localStorage.getItem(uiModeLocalKey) || 'dark')
function handleChangeUiMode(val: string) {
  commonStore.isDarkMode = val === 'dark'
  document.documentElement.setAttribute('class', val)
  localStorage.setItem(uiModeLocalKey, val)
  ElMessage.success('设置成功')
}

const nicknameColorShow = ref(false)
const bindEmailShow = ref(false)
const changePasswordShow = ref(false)
const setPrivacyShow = ref(false)
</script>

<template>
  <div class="user-page page">
    <div class="user-card">
      <div class="user-info border-wrap info-card">
        <h4>用户信息</h4>
        <p><span>用户ID：</span>{{ userStore.id }}</p>
        <p><span>用户名：</span>{{ userStore.username }}</p>
        <p>
          <span>昵称：</span><span class="nickname" :style="{ background: userStore.nicknameColor }">{{ userStore.nickname }}</span>
        </p>
        <p><span>金币：</span>{{ formatNumberWithCommas(userStore.gold) }}</p>
        <p><span>钻石：</span>{{ formatNumberWithCommas(userStore.diamond) }}</p>
        <p><span>水晶：</span>{{ formatNumberWithCommas(userStore.crystal) }}</p>
        <p>
          <span>会员等级：</span><span>{{ userStore.vipLevel }}</span>
        </p>

        <p>
          <span>注册时间：</span><span>{{ dayjs(userStore.created).format('YYYY-MM-DD') }}</span>
        </p>
      </div>
      <UserAttr style="width: 50%" />
    </div>
    <div class="user-action">
      <p style="display: flex; align-items: center; font-size: 12px">
        <span style="flex-shrink: 0">调整UI主题：</span>
        <el-select size="small" v-model="uiMode" @change="handleChangeUiMode">
          <el-option label="黑暗模式（推荐）" value="dark"></el-option>
          <el-option label="明亮模式（优化中，不推荐）" value="light"></el-option>
        </el-select>
      </p>
      <p style="display: flex; align-items: center; font-size: 12px">
        <span style="flex-shrink: 0">调整UI大小：</span>
        <el-select size="small" v-model="uiZoom" @change="handleChangeUiZoom">
          <el-option label="85%" value="0.85"></el-option>
          <el-option label="90%" value="0.9"></el-option>
          <el-option label="95%" value="0.95"></el-option>
          <el-option label="100%（推荐）" value="1"></el-option>
          <el-option label="105%" value="1.05"></el-option>
          <el-option label="110%" value="1.1"></el-option>
          <el-option label="115%" value="1.15"></el-option>
        </el-select>
      </p>
      <div class="btn-wrap">
        <CommonBtn type="success" @done="getToken" v-if="envMode === 'test'">获取唯一标识</CommonBtn>
        <CommonBtn type="primary" @done="handleUseCdkey">使用兑换码</CommonBtn>
        <CommonBtn type="primary" @done="bindEmailShow = true" v-show="!userStore.email">绑定邮箱</CommonBtn>
        <CommonBtn type="primary" @done="setPrivacyShow = true">修改隐私设置</CommonBtn>
        <CommonBtn type="primary" @done="handleUpdateNickname">修改昵称</CommonBtn>
        <CommonBtn type="primary" @done="nicknameColorShow = true">修改昵称颜色</CommonBtn>
        <CommonBtn type="warning" @done="handleChangePassword">修改密码</CommonBtn>
        <CommonBtn type="danger" @done="handleLogout" style="margin-bottom: 0">退出登录</CommonBtn>
      </div>
    </div>

    <el-dialog v-model="nicknameColorShow" title="修改昵称颜色" width="100%" style="max-width: 500px" destroy-on-close>
      <NicknameColor />
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="nicknameColorShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <el-dialog v-model="bindEmailShow" title="绑定邮箱" width="100%" style="max-width: 500px" destroy-on-close>
      <BindEamil @success="bindEmailShow = false" />
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="bindEmailShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <el-dialog v-model="changePasswordShow" title="修改密码" width="100%" style="max-width: 500px" destroy-on-close>
      <ChangePassword @success="changePasswordShow = false" />
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="changePasswordShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <el-dialog v-model="setPrivacyShow" title="修改隐私" width="100%" style="max-width: 500px" destroy-on-close>
      <SetPrivacy @success="setPrivacyShow = false" />
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="setPrivacyShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.user-page {
  height: auto;
  .user-card {
    display: flex;
    margin-bottom: 10px;
    .user-info {
      width: 50%;
      margin-right: 10px;
    }
  }
  .user-action {
    padding: 10px 8px;
  }
}
</style>
